import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Trophy, Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

interface UseEnhancedGameNotificationsProps {
  userId?: string;
  onNotification?: (type: string, data: any) => void;
}

export const useEnhancedGameNotifications = ({
  userId,
  onNotification
}: UseEnhancedGameNotificationsProps) => {
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('enhanced-game-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'venue_games'
        },
        (payload) => {
          const gameData = payload.new as any;
          const eventType = payload.eventType;
          
          if (eventType === 'UPDATE') {
            // Game status changes
            if (gameData.game_status === 'confirmed') {
              toast.success('🎮 Game Confirmed!', {
                description: 'Countdown timer has started. Finalize your roster before the deadline.',
                icon: <CheckCircle className="h-4 w-4" />,
                duration: 5000
              });
            } else if (gameData.game_status === 'forfeited') {
              toast.error('⚠️ Game Forfeited', {
                description: gameData.forfeit_reason || 'Team failed to meet requirements.',
                icon: <AlertTriangle className="h-4 w-4" />,
                duration: 8000
              });
            } else if (gameData.game_status === 'completed') {
              toast.info('🏆 Game Completed', {
                description: 'Check the leaderboard for updated rankings!',
                icon: <Trophy className="h-4 w-4" />,
                duration: 6000
              });
            }
          } else if (eventType === 'INSERT') {
            toast.info('🆕 New Game Available', {
              description: 'A new game has been created and is looking for teams.',
              icon: <Trophy className="h-4 w-4" />,
              duration: 4000
            });
          }
          
          onNotification?.(eventType, gameData);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_participants'
        },
        (payload) => {
          const participantData = payload.new as any;
          const eventType = payload.eventType;
          
          if (eventType === 'INSERT') {
            toast.info('👥 New Player Added', {
              description: 'A player has joined the game roster.',
              icon: <Users className="h-4 w-4" />,
              duration: 3000
            });
          } else if (eventType === 'UPDATE') {
            if (participantData.participation_status === 'ready_to_play') {
              toast.success('✅ Player Ready', {
                description: 'A player confirmed they\'re ready to play.',
                icon: <CheckCircle className="h-4 w-4" />,
                duration: 3000
              });
            } else if (participantData.participation_status === 'no_show') {
              toast.warning('❌ Player No-Show', {
                description: 'A player has been marked as no-show.',
                icon: <AlertTriangle className="h-4 w-4" />,
                duration: 4000
              });
            }
          }
          
          onNotification?.('participant_' + eventType, participantData);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_confirmations'
        },
        (payload) => {
          const confirmationData = payload.new as any;
          const eventType = payload.eventType;
          
          if ((eventType === 'INSERT' || eventType === 'UPDATE') && confirmationData.confirmation_status) {
            toast.success('🤝 Team Confirmed', {
              description: `${confirmationData.team_side} team has confirmed participation.`,
              icon: <CheckCircle className="h-4 w-4" />,
              duration: 4000
            });
          }
          
          onNotification?.('confirmation_' + eventType, confirmationData);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_notifications'
        },
        (payload) => {
          const notification = payload.new as any;
          
          if (notification?.recipient_id === userId) {
            const notificationType = notification?.notification_type;
            
            if (notificationType === 'game_reminder') {
              toast.info('⏰ Game Reminder', {
                description: 'Your game is starting soon! Make sure you\'re ready.',
                icon: <Clock className="h-4 w-4" />,
                duration: 6000
              });
            } else if (notificationType === 'roster_deadline') {
              toast.warning('⏰ Roster Deadline Approaching', {
                description: 'You have limited time to finalize your roster!',
                icon: <Clock className="h-4 w-4" />,
                duration: 8000
              });
            } else if (notificationType === 'roster_deadline_missed') {
              toast.error('⏰ Roster Deadline Passed', {
                description: 'Your team may forfeit if minimum players not met.',
                icon: <AlertTriangle className="h-4 w-4" />,
                duration: 10000
              });
            }
          }
          
          onNotification?.('notification', notification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, onNotification]);
};